// Angular
import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';

// Local library
import {
  RaceStringToRangePipe,
  GetCurrentRacePipe,
  GetRaceStatusPipe,
  SortEventsPipe,
  GetEventStatusPipe,
  GetTrackTypePipe,
  ConvertToRangePipe,
  LegHeightDirective,
  OrdinalsPipe,
  HorseWeightPipe,
  NumberWithCommasPipe,
  CountdownPipe,
  FormatAmountPipe,
  SanitizerPipe,
  UnsubscribeObservableService,
  MatNotificationService,
  TranslatePipe
} from '@amwager/util';
import { CommonFacade, PermissionService, SessionService } from '@amwager/data-access';
import {
  GUEST,
  IGetEventsDetailsResponse,
  IMoreTrackInfo,
  IPool,
  IRace,
  LoggerService
} from '@amwager/util-ts';
import { RaceFinalComponent } from '@amwager/wager/ui-ion';
import {
  ISelectedRace,
  ISelectedRaceMatrix,
  RaceService,
  WagerFacade
} from '@amwager/wager/data-access';
import { AccountFacade } from '@amwager/account/data-access';

// Angular Material Modules
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

// Third party
// import * as _ from 'lodash';
import _ from 'lodash';
import { take, takeUntil } from 'rxjs';

@Component({
  selector: 'feature-mat-pool',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RaceStringToRangePipe,
    GetCurrentRacePipe,
    GetRaceStatusPipe,
    SortEventsPipe,
    GetEventStatusPipe,
    GetTrackTypePipe,
    ConvertToRangePipe,
    LegHeightDirective,
    OrdinalsPipe,
    HorseWeightPipe,
    // RunnerComponent,
    RaceFinalComponent,
    NumberWithCommasPipe,
    // MatrixComponent,
    CountdownPipe,
    FormatAmountPipe,
    SanitizerPipe,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    TranslatePipe
  ],
  providers: [DatePipe],
  templateUrl: './pool.component.html',
  styleUrls: ['./pool.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoolComponent extends
UnsubscribeObservableService implements OnInit, OnChanges {
  @Input() selectedEvent!: IGetEventsDetailsResponse;
  @Input() showMoreTrackInfo = false;
  @Output() doGetPoolFn = new EventEmitter<IPool>();

  selectedRace!: ISelectedRace;
  selectedPool!: IPool | undefined;
  pool!: string;
  modifier!: string;
  dblPkArray!: any;
  PoolIds: string[] = ['EX','QU','TRL','TRI','SPR','OMN','PEN','HEX'];

  isExoticSelections!: boolean;

  // Canonical chip order (legacy parity). Store keeps raw API order;
  // unknown pool ids sort to the end.
  poolSortArray: string[] = [
    'WIN', 'PLC', 'SHW', 'EX', 'QU', 'TRI', 'TRL', 'SPR', 'PEN', 'HEX',
    'DBL', 'PK3', 'PK4', 'PK5', 'PK6', 'PK7', 'PK8', 'PK9', 'PK10', 'PK11',
    'PK12', 'QD', 'DQ', 'TT', 'TS'
  ];

  runId!: string;
  eventId!: string;
  raceId!: string;

  private previousSelectedEventID!: string;
  private previousSelectedRaceID!: string;

  // `<eventId>-<raceId>-<runId>` of the race the defaults were last
  // applied for. Lets us tell a real race/event switch apart from a
  // same-race pusher refresh so the pool selection isn't reset.
  private currentRaceKey?: string;
  private emittedRaceKey?: string;

  constructor(
    readonly cdRef: ChangeDetectorRef,
    readonly permissionService: PermissionService,
    private readonly sessionService: SessionService,
    readonly router: Router,
    private readonly elementRef: ElementRef,
    readonly wagerFacade: WagerFacade,
    private readonly accountFacade: AccountFacade,
    private readonly commonFacade: CommonFacade,
    private readonly matNotificationService: MatNotificationService,
    readonly raceService: RaceService,
    private readonly loggerService: LoggerService
  ) {
    super();

    this.wagerFacade.toUpdateGeneralEvents$().subscribe((toUpdate) => {
      if (!toUpdate) return;

      this.wagerFacade.getSelectedRace$()
        .pipe(take(1))
        .subscribe((race?: ISelectedRace) => {
          if (!race || !race?.RaceId || !race?.runId || !race?.eventId) return;

          if (
            this.raceId === String(race.RaceId)
            && this.runId === String(race.runId)
            && this.eventId === String(race.eventId)
          ) return;

          this.selectedRace = race;
        });
    });

    this.accountFacade.isGetAccountPreferencesSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {

        if (callback === undefined) return;

        const response = callback?.response?.AccountPreferences;
        this.isExoticSelections = response?.IsExoticSelections;
        this.cdRef.markForCheck();
      });

    this.wagerFacade.getSelectedRace$().subscribe((race?: ISelectedRace) => {

        if (!race || !race?.RaceId || !race?.runId || !race?.eventId) return;

        this.selectedRace = race;

        const raceKey = `${race.eventId}-${race.RaceId}-${race.runId}`;
        const pools = (this.selectedRace as IRace)?.Pools || [];

        // Default to the first pool only on a real race/event change.
        // Same-race pusher refresh keeps the user's pool (rebound to
        // the fresh pool object so its data stays current).
        if (raceKey !== this.currentRaceKey) {
          this.currentRaceKey = raceKey;
          this.selectedPool = pools[0];
        } else {
          this.selectedPool =
            _.find(pools, ['PoolId', this.selectedPool?.PoolId]) || this.selectedPool;
        }

        this.cdRef.markForCheck();
      });

  }

  /**
   * Init on load
   * @returns {void}
   */
  ngOnInit(): void {
    null;
  }

  /**
   * Init after view loaded
   * @param {any} changes listen to data changes
   * @returns {void}
   */
  ngOnChanges(changes: SimpleChanges): void {

    if (
      changes['selectedEvent'] !== undefined &&
      changes['selectedEvent'].currentValue !== undefined
    ) {

      // proceed if selected event ID is not the same from Parent input
      // skip if selected event ID is the same from Parent input
      if (
        // if different event ID
        changes['selectedEvent'].currentValue.id !== this.previousSelectedEventID
      ) {
        this.previousSelectedEventID = changes['selectedEvent'].currentValue.id;
        // this.raceService.setDefaultValues(
        //   this.selectedRace?.RaceId || 0,
        //   this.swipeRaceSectionTemplateRef,
        //   this.doSetRaceStatusFn
        // );

      } else this.loggerService.log('POOL:onchanges:POOL:ERROR',);

      if (
        changes['selectedEvent'].currentValue?.ToteResponse?.EventDetail?.currentRace?.RaceId
        !== this.previousSelectedRaceID
      ) {
        this.previousSelectedRaceID
          = changes['selectedEvent'].currentValue?.ToteResponse?.EventDetail?.currentRace?.RaceId;
        // this.raceService.setDefaultValues(
        //   this.selectedRace?.RaceId || 0,
        //   this.swipeRaceSectionTemplateRef,
        //   this.doSetRaceStatusFn
        // );

      }

      this.wagerFacade.getSelectedRace$().subscribe((race?: ISelectedRace) => {

        if (!race || !race?.RaceId || !race?.runId || !race?.eventId) return;

        this.selectedRace = race;
        this.cdRef.markForCheck();
      });

    }

    if (!_.isEmpty(this.selectedRace?.Pools)) {
      const raceKey =
        `${this.selectedRace?.eventId}-${this.selectedRace?.RaceId}-${this.selectedRace?.runId}`;

      // Emit the default pool to the parent only on a real race/event
      // change — not on same-race pusher refreshes that would reset the
      // user's pool (and cascade a reset to modifier / bet amount).
      if (raceKey !== this.emittedRaceKey) {
        this.emittedRaceKey = raceKey;
        this.selectedPool = _.first(this.selectedRace?.Pools) || undefined;

        const {
          dblPkArray,
          selectedRace
        } = this.raceService.fetchDblPkArrayAndNone(
          this.selectedPool,
          this.selectedEvent,
          this.selectedRace,
          'web'
        );

        const data = {
          pool: _.first(this.selectedRace?.Pools),
          selectedRace: selectedRace,
          dblPkArray: dblPkArray
        }

        this.doGetPoolFn.emit(data as any);
      }
    }

    this.cdRef.markForCheck();
  }

  /**
   * Rows for the "more track info" panel. Mirrors the legacy
   * `#more-track-info` list: only fields the tote returned are shown,
   * so the panel stays empty rather than rendering blank labels.
   * @returns {IMoreTrackInfo[]} Populated rows in display order
   */
  get moreTrackInfo(): IMoreTrackInfo[] {
    const race = this.selectedRace;
    if (!race) return [];

    const purse = Number(race.Purse);
    const rows: IMoreTrackInfo[] = [
      // {
      //   key: 'ui.wager.purse',
      //   label: 'Purse',
      //   value: purse > 0 ? `$${purse.toLocaleString('en-US')}` : ''
      // },
      { key: 'ui.wager.race.changes', label: 'Race Changes', value: race.RaceChanges ?? '' },
      { key: 'ui.wager.conditions', label: 'Conditions', value: race.Conditions ?? '' },
      { key: 'ui.wager.sex', label: 'Sex', value: race.Sex ?? '' },
      { key: 'ui.wager.age', label: 'Age', value: race.Age ?? '' },
      { key: 'ui.wager.claim', label: 'Claim', value: race.Claim ?? '' }
    ];

    return rows.filter((row) => String(row.value).trim().length > 0);
  }

  /**
   * Chip-bar pools in canonical order (sorted copy, no state mutation).
   * @returns {IPool[]} Pools in display order
   */
  getSortedPools(): IPool[] {
    const pools = (this.selectedRace as IRace)?.Pools;
    if (!pools?.length) return [];

    // _.sortBy returns a new array; unknown ids -> Infinity (sort last).
    return _.sortBy(pools, (pool: IPool) => {
      const index = this.poolSortArray.indexOf(pool?.PoolId);
      return index === -1 ? Infinity : index;
    });
  }

  /**
   * Set event pool
   * @param {any} pool current DOM element
   * @returns {void}
   */
  selectPool(pool: any): void {

    const currentPool =  pool?.PoolName;
    const foundObject: IPool = _.find(this.selectedRace?.Pools, ['PoolName', currentPool]) as IPool;

    if (
      !this.isExoticSelections ||
      !this.raceService.checkExotic(
        foundObject.PoolId, this.isExoticSelections, this.PoolIds, this.selectedPool
      )
    ) {
      this.raceService.clearActiveAllLeg(this.elementRef);
      this.raceService.clearLegRoundSelector(this.elementRef);
    }

    this.selectedPool = foundObject;
    this.pool = currentPool;

    const {
      dblPkArray,
      selectedRace
    } = this.raceService.fetchDblPkArrayAndNone(
      foundObject,
      this.selectedEvent,
      this.selectedRace,
      'web'
    );

    // const startsWithPK = /^PK/.test(foundObject.PoolId || '');
    // const wordWithPK = /^PICK/.test(foundObject.PoolName || '');

    // if (this.selectedEvent !== undefined && this.selectedRace !== undefined) {

    //   if (!this.raceService.checkDataDBLPK(this.selectedRace)) {
    //     this.cdRef.markForCheck();
    //     return;
    //   }

    //   if (startsWithPK || wordWithPK) {
    //     if (
    //       this.raceService.extractNumber(foundObject.PoolId) &&
    //       this.raceService.extractNumber(foundObject.PoolId) !== Number(NaN)
    //     ) {
    //       this.dblPkArray = this.raceService.storeDBLPKraces(
    //         this.selectedEvent.ToteResponse.EventDetail.Races,
    //         this.selectedRace.RaceId as number,
    //         this.raceService.extractNumber(foundObject.PoolId)
    //       );

    //     }
    //   }
    //   else if (foundObject.PoolId === 'GSL') {
    //     this.dblPkArray = this.raceService.storeDBLPKraces(
    //       this.selectedEvent.ToteResponse.EventDetail.Races,
    //       this.selectedRace.RaceId as number,
    //       (4)
    //     );
    //   }
    //   else if (foundObject.PoolId === 'DBL') {
    //     this.dblPkArray = this.raceService.storeDBLPKraces(
    //       this.selectedEvent.ToteResponse.EventDetail.Races,
    //       this.selectedRace.RaceId as number,
    //       (2)
    //     );
    //   } else {
    //     this.dblPkArray = '';
    //     const updatedRunners = this.raceService.processFillInRunnersNonDblPk(this.selectedRace.Runners);
    //     const updatedRace = _.cloneDeep(this.selectedRace);
    //     updatedRace.Runners = updatedRunners;
    //     this.selectedRace = updatedRace;
    //   }
    // }

    // if (
    //   foundObject.PoolId === 'GSL' ||
    //   foundObject.PoolId === 'DBL' ||
    //   startsWithPK || wordWithPK
    // ) {
    //   this.dblPkArray = this.raceService.processFillInRunners(this.dblPkArray);
    // }

    const data = {
      pool: this.selectedPool,
      dblPkArray: dblPkArray,
      selectedRace: selectedRace
    }

    this.doGetPoolFn.emit(data as any);
    this.cdRef.markForCheck(); // re-render for template
  }

}
